import NextImage from 'next/image'
import { useMemo } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { Button } from '@wartek-id/button-v1'
import { Checkbox, CheckboxGroup } from '@wartek-id/checkbox-v1'
import { Icon } from '@wartek-id/icon-v2'
import { Select } from '@wartek-id/select'
import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'

import {
  useAPIGetFollowUpTargets,
  useAPIPelaksanaanProgress,
} from 'api/kinerja/useAPIKinerja'
import {
  Card,
  CardBody,
  CardHeader,
} from 'app/KinerjaPage/pages/PelaksanaanV2/components/Card'
import { SelectSupportNeed } from './SelectSupportNeed'
import useDemoContext from 'app/KinerjaPage/hooks/useDemoContext'
import { useKinerjaTipe } from 'app/KinerjaShared/hooks/useKinerjaTipe'

import type { MultiPeriodParams } from 'types/kinerja'
import type { HeadingLevel } from 'types/typography'
import type {
  IFollowUpDocumentForm,
  IFollowUpTargetItem,
} from 'types/kinerja-pelaksanaan'

export type FormPilihanBelajarRuangGTKProps = {
  followUpTargets: Array<IFollowUpTargetItem>
  headingLevel?: HeadingLevel
  hideIfOptional?: boolean
  isDisabled?: boolean
  isReadOnly?: boolean
  periodParams?: MultiPeriodParams
  ptkId?: string // For KS
}
export function FormPilihanBelajarRuangGTK({
  followUpTargets,
  headingLevel = 'h2',
  hideIfOptional = false,
  isDisabled = false,
  isReadOnly = false,
  periodParams,
  ptkId,
}: FormPilihanBelajarRuangGTKProps) {
  const { isDemoMode } = useDemoContext()
  const formContext = useFormContext<IFollowUpDocumentForm>()

  const { tipe } = useKinerjaTipe()
  const { data: dataProgress, isLoading: isLoadingPelaksanaanProgress } =
    useAPIPelaksanaanProgress({
      periodParams,
      ptkId,
      tipe,
      opts: {
        staleTime: 60 * 60 * 1000, // 1 hour
      },
      isDemoMode,
    })

  const {
    data: dataTujuanTindakLanjut,
    isLoading: isLoadingTujuanTindakLanjut,
  } = useAPIGetFollowUpTargets(
    { praktikPembelajaranId: dataProgress?.data?.contentId },
    { enabled: !!dataProgress?.data?.contentId, useErrorBoundary: true },
    isDemoMode
  )

  const isFetchingTujuanTindakLanjut =
    isLoadingPelaksanaanProgress || isLoadingTujuanTindakLanjut

  const renderTujuanShimmer = () => {
    return (
      <div className="flex flex-row gap-4 flex-wrap">
        <Shimmer className="!w-72 max-w-full !h-24" />
        <Shimmer className="!w-72 max-w-full !h-24" />
        <Shimmer className="!w-72 max-w-full !h-24" />
      </div>
    )
  }

  if (!formContext) {
    throw new Error(
      'FormPilihanBelajarRuangGTK must be used within `<FormProvider />`'
    )
  }

  const { control, watch } = formContext

  const values = watch(['followUpTargets', 'supportNeed'])

  const isRequired = useMemo(() => {
    for (const value of values) {
      if (Array.isArray(value)) {
        if (!!value.length) return true
      } else {
        if (!!value) return true
      }
    }

    return false
  }, [values])

  if (!isRequired && hideIfOptional) {
    return null
  }

  return (
    <Card>
      <CardHeader className="flex space-x-4 py-4 items-center">
        <NextImage
          src="/illustrations/img-smartphone-star.png"
          width="64"
          height="64"
        />
        <Text variant="heading-md" className="flex-1" as={headingLevel}>
          Opsional: Pilihan Belajar dari Ruang GTK
        </Text>
      </CardHeader>

      <CardBody className="p-6 space-y-3">
        <fieldset className="space-y-2">
          <Text variant="heading-sm">
            Apa tujuan tindak lanjut yang ingin Anda lakukan untuk meningkatkan
            kualitas Praktik Kinerja Anda?
          </Text>
          <div className="space-y-1 max-w-full">
            <Text as="label" color="subdued">
              Pilih minimal 1 tujuan tindak lanjut (berdasarkan Perdirjen GTK
              Nomor 2626 Tahun 2023 Tentang Model Kompetensi Guru)
            </Text>
            {isFetchingTujuanTindakLanjut && renderTujuanShimmer()}
            {!isReadOnly && isDisabled && !ptkId && (
              <div className="space-y-2">
                {followUpTargets.map((thisTarget, index) => (
                  <div
                    key={`module_${index}`}
                    className="p-3 border rounded border-subdued w-full flex justify-between items-center"
                  >
                    <div className="flex flex-col space-y-1 w-9/12">
                      <Text variant="action">
                        {thisTarget?.learningModule?.title}
                      </Text>
                      <Text color="subdued">{thisTarget?.subIndicator}</Text>
                    </div>
                    <Button
                      color="blue"
                      size="lg"
                      as="a"
                      href={thisTarget?.learningModule?.url ?? ''}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Pelajari modul <Icon color="inverse">chevron_right</Icon>
                    </Button>
                  </div>
                ))}
              </div>
            )}
            {isReadOnly ? (
              <div className="space-y-3">
                {followUpTargets.map((target) => (
                  <div
                    className="p-3 border rounded border-subdued w-full"
                    key={target.id}
                  >
                    <Text variant="action">{target.subIndicator}</Text>
                    <Text color="subdued">{target.learningModule?.title}</Text>
                  </div>
                ))}
              </div>
            ) : (
              ((isDisabled && ptkId) || !isDisabled) && (
                <Controller
                  control={control}
                  name="followUpTargets"
                  rules={{
                    required: isRequired,
                    validate: {
                      required: (checkboxData) =>
                        !checkboxData.filter(
                          (thisCheckboxData) =>
                            !thisCheckboxData.learningModuleId
                        ).length,
                    },
                  }}
                  render={({ field: { onChange, value, name } }) => {
                    const mappedValue = value?.map((thisValue) => thisValue.id)
                    return (
                      <CheckboxGroup
                        name={name}
                        value={mappedValue}
                        onChange={(checkboxValue) => {
                          const updated = checkboxValue.map(
                            (thisCheckboxValue) => {
                              const found = value.find(
                                (prevSelectedValue) =>
                                  prevSelectedValue.id === thisCheckboxValue
                              )
                              if (found) return found
                              return {
                                id: thisCheckboxValue,
                                learningModuleId: '',
                              }
                            }
                          )
                          onChange(updated)
                        }}
                        className="flex flex-row gap-4 flex-wrap"
                      >
                        {dataTujuanTindakLanjut?.data?.map((item) => (
                          <Checkbox
                            key={item.id}
                            value={item.id}
                            labelPosition="start"
                            className="p-3 border rounded border-subdued w-full"
                            disabled={isDisabled}
                          >
                            <div
                              className="flex flex-col space-y-2 max-w-2xl"
                              style={{ width: '60vw' }}
                            >
                              <Text as="span" variant="action">
                                {item.subIndicator}
                              </Text>
                              <Text as="span">
                                Pilih modul Pelatihan Mandiri
                              </Text>
                              <Select
                                placeholder="Pilih di sini"
                                value={
                                  value?.find(
                                    (thisValueData) =>
                                      thisValueData.id === item.id
                                  )?.learningModuleId || ''
                                }
                                onChange={(selectValue) => {
                                  const modifiedValue = [...value]
                                  const modifiedIndex = modifiedValue.findIndex(
                                    (thisValueData) =>
                                      thisValueData.id === item.id
                                  )
                                  modifiedValue[modifiedIndex] = {
                                    id: item.id,
                                    learningModuleId: selectValue.target.value,
                                  }
                                  onChange(modifiedValue)
                                }}
                                isDisabled={
                                  isDisabled || !mappedValue.includes(item.id)
                                }
                              >
                                <option
                                  disabled
                                  value=""
                                  className="text-disabled"
                                >
                                  Pilih di sini
                                </option>
                                {item?.learningModules?.map((item, i) => (
                                  <option key={i} value={item.id}>
                                    {item.title}
                                  </option>
                                ))}
                              </Select>
                            </div>
                          </Checkbox>
                        ))}
                      </CheckboxGroup>
                    )
                  }}
                />
              )
            )}
          </div>
        </fieldset>

        <SelectSupportNeed
          fieldName="supportNeed"
          isDisabled={isDisabled}
          isRequired={isRequired}
          isReadOnly={isReadOnly}
        />
      </CardBody>
    </Card>
  )
}
